import {
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  createElement,
} from "react";
import { cn } from "../../utils";

type SpacingSize =
  | "none" // 0px
  | "xxs" // 2px
  | "xs" // 4px
  | "sm" // 6px
  | "md" // 8px
  | "lg" // 12px
  | "xl" // 16px
  | "2xl" // 20px
  | "3xl" // 24px
  | "4xl" // 32px
  | "5xl" // 40px
  | "6xl" // 48px
  | "7xl" // 64px
  | "8xl"; // 80px

interface StackProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  wrap?: boolean;
  justify?: "start" | "end" | "center" | "between" | "around" | "evenly";
  align?: "start" | "end" | "center" | "baseline" | "stretch";
  flex?: string;
  gap?: SpacingSize;
  direction?: "row" | "column";
  tag?: keyof JSX.IntrinsicElements;
}

export const Stack = forwardRef(function Stack(
  {
    children,
    wrap = false,
    justify,
    align,
    flex,
    gap = "md",
    className,
    direction = "row",
    tag = "div",
    ...rest
  }: StackProps,
  ref: ForwardedRef<HTMLElement>,
): JSX.Element {
  const getGapClass = (size: SpacingSize) => {
    const gapSizes = {
      none: "al-gap-0",
      xxs: "al-gap-0.5", // 2px
      xs: "al-gap-1", // 4px
      sm: "al-gap-1.5", // 6px
      md: "al-gap-2", // 8px
      lg: "al-gap-3", // 12px
      xl: "al-gap-4", // 16px
      "2xl": "al-gap-5", // 20px
      "3xl": "al-gap-6", // 24px
      "4xl": "al-gap-8", // 32px
      "5xl": "al-gap-10", // 40px
      "6xl": "al-gap-12", // 48px
      "7xl": "al-gap-16", // 64px
      "8xl": "al-gap-20", // 80px
    };
    return gapSizes[size];
  };

  const classes = cn(
    "al-flex",
    // Direction
    direction === "column" ? "al-flex-col" : "al-flex-row",
    // Wrap
    wrap ? "al-flex-wrap" : "al-flex-nowrap",
    // Justify
    {
      "al-justify-start": justify === "start",
      "al-justify-end": justify === "end",
      "al-justify-center": justify === "center",
      "al-justify-between": justify === "between",
      "al-justify-around": justify === "around",
      "al-justify-evenly": justify === "evenly",
    },
    // Align
    {
      "al-items-start": align === "start",
      "al-items-end": align === "end",
      "al-items-center": align === "center",
      "al-items-baseline": align === "baseline",
      "al-items-stretch": align === "stretch",
    },
    // Flex
    flex && `al-flex-[${flex}]`,
    // Gap
    getGapClass(gap),
    className,
  );

  return createElement(
    tag,
    {
      ...rest,
      className: classes,
      ref,
    },
    children,
  );
});
